/*
 * Checkout Flow — <cf-hero-scene> : tunnel de particules en Canvas 2D, les logos Shopify et SumUp
 * convergent vers le logo Checkout Flow. Décoratif (aria-hidden) : le texte et le CTA sont en HTML.
 * - Mode « séquence immersive » (data-sequence) : la scène est fixée et avance avec le défilement
 *   (tunnel, rencontre Shopify / SumUp, onde, carte de paiement, paiement accepté) ; légendes par bandes.
 * - Mode standard : courte animation d'entrée, le défilement accélère légèrement la caméra.
 * - Pas de WebGL : un seul contexte 2D par héro.
 * - Suspendu hors écran et onglet masqué ; image fixe en mouvement réduit.
 * - Perte de contexte : repli sur la composition statique HTML.
 */
(() => {
  const start = () => {
    const CF = window.CF;
    if (!CF || customElements.get('cf-hero-scene')) return;
    const { clamp, smooth, motion, loop, observeVisible, onScroll, live, firstBind, release } = CF;

    class CFHeroScene extends HTMLElement {
      connectedCallback() {
        if (!live(this)) return; // simple déplacement dans l'éditeur : la scène continue
        this.canvas = this.querySelector('canvas');
        this.zone = this.querySelector('.cf-hero__zone');
        this.section = this.closest('.cf-hero');
        this.seqEl = this.closest('.cf-hero__seq');
        this.seq = this.getAttribute('data-sequence') === 'true' && !!this.section && this.section.classList.contains('cf-hero--seq');
        this.ctx = this.canvas && this.canvas.getContext ? this.canvas.getContext('2d') : null;
        if (!this.ctx) { this.dropSequence(); return; } // repli statique déjà visible
        this.inner = this.querySelector('.cf-hero__inner');
        this.bands = [...this.querySelectorAll('[data-band]')].map((el) => {
          const [a, b] = el.getAttribute('data-band').split(',').map(Number);
          return { el, a, b };
        });
        this.bar = this.querySelector('.cf-hero__progress i');
        this.tip = this.querySelector('.cf-hero__scrolltip');
        this.target = 0; this.cur = 0; this.band = 0;
        this.withLogos = this.getAttribute('data-logos') !== 'false' && !!this.zone;
        this.density = this.getAttribute('data-density') || 'normal';
        this.imgs = { shop: new Image(), sum: new Image() };
        this.imgs.shop.src = this.getAttribute('data-shopify-src');
        this.imgs.sum.src = this.getAttribute('data-sumup-src');
        this.lbl = { secure: this.getAttribute('data-l-secure') || '', processing: this.getAttribute('data-l-processing') || '', confirmed: this.getAttribute('data-l-confirmed') || '' };
        this.imgs.shop.onload = this.imgs.sum.onload = () => { if (!this.running) this.drawFrame(performance.now()); };
        this.t0 = performance.now();
        this.sp = 0;
        this.px = 0; this.py = 0; this.mx = 0; this.my = 0;
        this.visible = false;
        this.buildStars();
        this.resize = this.resize.bind(this);
        this.tick = this.tick.bind(this);
        this.ro = new ResizeObserver(this.resize);
        this.ro.observe(this);
        this.resize();
        this.stopVis = observeVisible(this, (v) => { this.visible = v; this.sync(); }, '0px');
        this.unsubScroll = onScroll(() => this.onScroll());
        this.onMotion = () => {
          // passage en mode fixe au milieu de la séquence : on revient au début du héro (la page raccourcit)
          if (this.seq && motion() === 'off' && this.cur > 0.01 && this.cur < 0.99 && this.section) this.section.scrollIntoView({ block: 'start' });
          this.buildStars(); this.onScroll(); this.cur = this.target; this.resize(); this.sync();
        };
        document.addEventListener('cf:motion-change', this.onMotion);
        this.onPointer = (e) => { this.mx = e.clientX / window.innerWidth - 0.5; this.my = e.clientY / window.innerHeight - 0.5; };
        if (window.matchMedia('(pointer: fine)').matches) window.addEventListener('pointermove', this.onPointer, { passive: true });
        if (firstBind(this)) {
          this.canvas.addEventListener('contextlost', () => { this.lost = true; this.classList.remove('is-live'); this.dropSequence(); this.sync(); });
          this.canvas.addEventListener('contextrestored', () => { this.lost = false; this.sync(); });
        }
        this.classList.add('is-live');
        this.onScroll();
        this.cur = this.target;
        this.sync();
      }

      /* sans canvas : héro standard (la séquence fixée n'aurait rien à montrer) */
      dropSequence() {
        if (!this.seq || !this.section) return;
        this.seq = false;
        this.section.classList.remove('cf-hero--seq');
      }

      seqOn() { return this.seq && motion() !== 'off' && !this.lost; }

      updateBands(p) {
        let idx = 0;
        this.bands.forEach((b, i) => { if (p >= b.a && p < b.b) idx = i; });
        if (p < this.bands[0].b) idx = 0;
        if (idx !== this.band) {
          this.band = idx;
          this.bands.forEach((b, i) => b.el.classList.toggle('is-on', i === idx));
        }
        if (this.bar) this.bar.style.transform = `scaleY(${p.toFixed(3)})`;
        if (this.tip) this.tip.style.opacity = p > 0.03 ? '0' : '1';
      }

      disconnectedCallback() {
        release(this, () => {
          if (!this.ctx) return;
          loop.remove(this.tick); this.running = false;
          if (this.ro) this.ro.disconnect();
          if (this.stopVis) this.stopVis();
          if (this.unsubScroll) this.unsubScroll();
          document.removeEventListener('cf:motion-change', this.onMotion);
          window.removeEventListener('pointermove', this.onPointer);
        });
      }

      buildStars() {
        const small = window.innerWidth < 750;
        const m = motion();
        let n = small ? 150 : 320;
        if (this.density === 'low' || m === 'soft') n = Math.round(n * 0.55);
        if (this.density === 'none') n = 0;
        this.depth = 6;
        this.stars = [];
        for (let i = 0; i < n; i++) {
          const a = Math.random() * Math.PI * 2;
          const r = 0.25 + Math.pow(Math.random(), 0.7) * 1.6;
          this.stars.push({ x: Math.cos(a) * r, y: Math.sin(a) * r * 0.7, z: Math.random() * this.depth, c: Math.random() < 0.55, s: Math.random() * 0.8 + 0.4 });
        }
        if (this.lite) this.stars = this.stars.filter((_, i) => i % 2 === 0);
      }

      sync() {
        const animate = this.visible && motion() !== 'off' && !this.lost;
        if (animate && !this.running) { this.running = true; loop.add(this.tick); }
        else if (!animate && this.running) { this.running = false; this.lastTick = 0; loop.remove(this.tick); }
        if (!animate && !this.lost) this.render(performance.now(), true);
      }

      render(now, still) {
        if (this.seqOn()) { this.drawSeq(this.cur, now, still); this.updateBands(this.cur); }
        else this.drawFrame(now, still);
      }

      resize() {
        const small = window.innerWidth < 750;
        this.dpr = Math.min(window.devicePixelRatio || 1, small ? 1.25 : 2); // décor : une définition réduite suffit sur mobile
        const r = this.getBoundingClientRect();
        this.W = Math.max(1, Math.round(r.width));
        this.H = Math.max(1, Math.round(r.height));
        this.canvas.width = Math.round(this.W * this.dpr);
        this.canvas.height = Math.round(this.H * this.dpr);
        this.ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
        if (this.seqOn() && this.inner) {
          // séquence : la scène démarre sous le texte d'accueil, puis remonte au centre
          const bottom = this.inner.getBoundingClientRect().bottom - r.top;
          this.cy = Math.min(this.H * 0.8, (bottom + this.H) / 2);
          this.zoneH = Math.max(120, this.H - bottom);
          // écran court : pas assez de place sous le texte d'accueil, les logos n'apparaissent qu'au premier défilement
          this.crowd = this.H - bottom < 170;
        } else if (this.zone) {
          const z = this.zone.getBoundingClientRect();
          this.cy = z.top - r.top + z.height * 0.52;
          this.zoneH = z.height;
        } else {
          this.cy = this.H * 0.5;
          this.zoneH = this.H * 0.3;
        }
        if (!this.running) this.render(performance.now(), true);
      }

      onScroll() {
        if (this.seqOn() && this.seqEl) {
          const r = this.seqEl.getBoundingClientRect();
          const total = this.seqEl.offsetHeight - window.innerHeight;
          this.target = total > 0 ? clamp(-r.top / total, 0, 1) : 0;
          if (!this.running) { this.cur = this.target; this.render(performance.now(), true); }
          return;
        }
        const r = this.getBoundingClientRect();
        this.sp = clamp(-r.top / Math.max(1, r.height), 0, 1);
      }

      /* qualité adaptative : si les images coûtent trop cher (appareil lent), moins de particules et pas de flou d'ombre */
      tick(t) {
        this.skip = !this.skip;
        if (this.lite && this.skip) return; // appareil lent : 30 images/s suffisent pour un décor
        const t0 = performance.now();
        const hd = this.lastTick ? Math.min(70, Math.max(1, t - this.lastTick)) : 16.667; // temps écoulé depuis la dernière image dessinée
        this.lastTick = t;
        if (this.seqOn()) {
          this.speed = Math.abs(this.target - this.cur);
          this.cur += (this.target - this.cur) * loop.k(0.09, hd);
          if (Math.abs(this.target - this.cur) < 0.0002) this.cur = this.target;
          this.drawSeq(this.cur, t, false);
          this.updateBands(this.cur);
        } else this.drawFrame(t, false);
        const dt = performance.now() - t0;
        this.cost = this.cost === undefined ? dt : this.cost * 0.9 + dt * 0.1;
        this.frames = (this.frames || 0) + 1;
        if (!this.lite && this.frames > 6 && this.cost > 9) {
          this.lite = true;
          this.stars = this.stars.filter((_, i) => i % 2 === 0);
        }
      }

      glow(x, y, r, col, a) {
        const ctx = this.ctx;
        const g = ctx.createRadialGradient(x, y, 0, x, y, r);
        g.addColorStop(0, `rgba(${col},${a})`);
        g.addColorStop(0.35, `rgba(${col},${a * 0.35})`);
        g.addColorStop(1, `rgba(${col},0)`);
        ctx.fillStyle = g;
        ctx.beginPath(); ctx.arc(x, y, r, 0, Math.PI * 2); ctx.fill();
      }

      badge(x, y, r, img, bg, alpha, cover) {
        if (alpha <= 0.01) return;
        const ctx = this.ctx;
        ctx.save();
        ctx.globalAlpha = alpha;
        if (!this.lite) { ctx.shadowColor = 'rgba(0,200,255,0.55)'; ctx.shadowBlur = 30; }
        ctx.fillStyle = bg; ctx.beginPath(); ctx.arc(x, y, r, 0, Math.PI * 2); ctx.fill();
        ctx.shadowColor = 'transparent';
        ctx.lineWidth = 2; ctx.strokeStyle = 'rgba(255,255,255,0.55)'; ctx.stroke();
        if (img.complete && img.naturalWidth) {
          ctx.beginPath(); ctx.arc(x, y, r - 1, 0, Math.PI * 2); ctx.clip();
          if (cover) { const s = r * 2.05; ctx.drawImage(img, x - s / 2, y - s / 2, s, s); }
          else { const h = r * 1.12, w = h * img.naturalWidth / img.naturalHeight; ctx.drawImage(img, x - w / 2, y - h / 2 - r * 0.03, w, h); }
        }
        ctx.restore();
      }

      cfLogo(x, y, size, alpha) {
        if (alpha <= 0.01) return;
        const ctx = this.ctx;
        const k = size / 40;
        ctx.save();
        ctx.globalAlpha = alpha;
        ctx.translate(x - size / 2, y - size / 2);
        ctx.scale(k, k);
        [[4, 12, '#ffffff', 8, 23], [14, 8, '#006CFF', 18, 19], [24, 4, '#00C8FF', 28, 15]].forEach(([bx, by, col, cx, cy]) => {
          ctx.save();
          ctx.translate(cx, cy); ctx.rotate(38 * Math.PI / 180); ctx.translate(-cx, -cy);
          ctx.fillStyle = col;
          const w = 8, h = 22, rr = 4;
          ctx.beginPath();
          ctx.moveTo(bx + rr, by); ctx.lineTo(bx + w - rr, by); ctx.quadraticCurveTo(bx + w, by, bx + w, by + rr);
          ctx.lineTo(bx + w, by + h - rr); ctx.quadraticCurveTo(bx + w, by + h, bx + w - rr, by + h);
          ctx.lineTo(bx + rr, by + h); ctx.quadraticCurveTo(bx, by + h, bx, by + h - rr);
          ctx.lineTo(bx, by + rr); ctx.quadraticCurveTo(bx, by, bx + rr, by);
          ctx.fill();
          ctx.restore();
        });
        ctx.restore();
      }

      roundRect(x, y, w, h, r) {
        const ctx = this.ctx;
        ctx.beginPath();
        ctx.moveTo(x + r, y); ctx.lineTo(x + w - r, y); ctx.quadraticCurveTo(x + w, y, x + w, y + r);
        ctx.lineTo(x + w, y + h - r); ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
        ctx.lineTo(x + r, y + h); ctx.quadraticCurveTo(x, y + h, x, y + h - r);
        ctx.lineTo(x, y + r); ctx.quadraticCurveTo(x, y, x + r, y); ctx.closePath();
      }

      /* ---------- séquence immersive : p (0 → 1) = avancée dans la section fixée ---------- */
      drawSeq(p, now, still) {
        const ctx = this.ctx;
        if (!ctx || !this.W) return;
        const W = this.W, H = this.H;
        const soft = motion() === 'soft';
        const frozen = !!still;
        const t = frozen ? 0 : now;
        this.px += (this.mx - this.px) * 0.05;
        this.py += (this.my - this.py) * 0.05;
        const lerp = (a, b, k) => a + (b - a) * k;
        const lift = smooth((p - 0.16) / 0.3); // la scène quitte le bas de l'écran pour le centre
        const cx = W / 2 + (frozen ? 0 : this.px * 30);
        const cy = lerp(this.cy, H * 0.5, lift) + (frozen ? 0 : this.py * 18);
        const f = Math.min(W, H) * 0.9;
        ctx.clearRect(0, 0, W, H);

        const bg = ctx.createRadialGradient(cx, cy, 0, cx, cy, Math.max(W, H) * 0.78);
        bg.addColorStop(0, '#07183A'); bg.addColorStop(0.55, '#040E20'); bg.addColorStop(1, '#020A14');
        ctx.fillStyle = bg; ctx.fillRect(0, 0, W, H);

        /* tunnel de particules : la caméra avance avec le défilement, traînées pendant le mouvement */
        const cam = p * 9 + (frozen ? 0 : t * (soft ? 0.00005 : 0.00012));
        const speed = frozen ? 0 : (this.speed || 0) * 40;
        const D = this.depth;
        ctx.globalCompositeOperation = 'lighter';
        for (let i = 0; i < this.stars.length; i++) {
          const s = this.stars[i];
          const z = ((s.z - cam) % D + D) % D + 0.05;
          const sx = cx + (s.x / z) * f * 0.5;
          const sy = cy + (s.y / z) * f * 0.5;
          if (sx < -50 || sx > W + 50 || sy < -50 || sy > H + 50) continue;
          const near = 1 - z / D;
          const r = Math.max(0.4, s.s * near * 2.6);
          const al = near * near * 0.9;
          ctx.fillStyle = s.c ? `rgba(0,200,255,${al})` : `rgba(40,130,255,${al})`;
          if (!soft && speed > 0.02 && near > 0.35) {
            const z2 = z + Math.min(0.5, speed * 0.25);
            const tx = cx + (s.x / z2) * f * 0.5, ty = cy + (s.y / z2) * f * 0.5;
            ctx.strokeStyle = ctx.fillStyle; ctx.lineWidth = r; ctx.lineCap = 'round';
            ctx.beginPath(); ctx.moveTo(tx, ty); ctx.lineTo(sx, sy); ctx.stroke();
          } else {
            ctx.beginPath(); ctx.arc(sx, sy, r, 0, Math.PI * 2); ctx.fill();
          }
        }

        /* Shopify et SumUp se rejoignent (0 → 0.55) autour du logo Checkout Flow, puis s'effacent devant la carte */
        const join = smooth(p / 0.55);
        const clear = this.crowd ? smooth((p - 0.04) / 0.22) : 1;
        const fade = (1 - smooth((p - 0.62) / 0.12)) * clear;
        const unit = Math.min(W * 0.12, H * 0.09, 66);
        const finalGap = unit * 2.35;
        const spread = finalGap + (Math.min(W * 0.38, 540) - finalGap) * (1 - join);
        const ny = cy + (frozen ? 0 : Math.sin(t * 0.0012) * 6 * (1 - join));
        const lx = cx - spread, rx = cx + spread;
        const nodeR = unit * 0.62;
        if (fade > 0.01) {
          ctx.lineCap = 'round';
          for (let k = 0; k < 5; k++) {
            const off = (k - 2) * (unit * 0.24) * (1 - join * 0.7);
            const amp = (k % 2 ? -1 : 1) * unit * 1.1 * (1 - join * 0.85);
            const c1x = cx - spread * 0.3, c2x = cx + spread * 0.3;
            ctx.strokeStyle = `rgba(0,200,255,${(0.18 + 0.12 * join) * fade})`;
            ctx.lineWidth = 1.2;
            ctx.beginPath(); ctx.moveTo(lx, ny + off); ctx.bezierCurveTo(c1x, ny + off + amp, c2x, ny + off - amp, rx, ny + off); ctx.stroke();
            if (!frozen) {
              const u = (t * (soft ? 0.0002 : 0.00035) + k * 0.21) % 1, iu = 1 - u;
              const bx = iu * iu * iu * lx + 3 * iu * iu * u * c1x + 3 * iu * u * u * c2x + u * u * u * rx;
              const by = iu * iu * iu * (ny + off) + 3 * iu * iu * u * (ny + off + amp) + 3 * iu * u * u * (ny + off - amp) + u * u * u * (ny + off);
              this.glow(bx, by, 10 + 8 * join, '160,235,255', 0.9 * fade);
            }
          }
          this.glow(lx, ny, nodeR * 3.2, '0,108,255', 0.55 * fade);
          this.glow(rx, ny, nodeR * 3.2, '0,200,255', 0.55 * fade);
        }

        /* onde de connexion (0.52 → 0.8) */
        const wave = (p - 0.52) / 0.28;
        if (wave > 0 && wave < 1) {
          const wr = smooth(wave) * Math.max(W, H) * 0.7;
          ctx.strokeStyle = `rgba(0,200,255,${(1 - wave) * 0.8})`;
          ctx.lineWidth = 3 * (1 - wave) + 0.5;
          ctx.beginPath(); ctx.ellipse(cx, ny, wr, wr * 0.55, 0, 0, Math.PI * 2); ctx.stroke();
          this.glow(cx, ny, Math.min(W, H) * 0.35 * (1 - wave * 0.5), '120,220,255', 0.7 * (1 - wave));
        }

        if (fade > 0.01) {
          ctx.globalCompositeOperation = 'source-over';
          this.badge(lx, ny, nodeR, this.imgs.shop, '#FFFFFF', fade, false);
          this.badge(rx, ny, nodeR, this.imgs.sum, '#000000', fade, true);
          ctx.globalCompositeOperation = 'lighter';
          ctx.lineWidth = 1.4;
          [lx, rx].forEach((nx, idx) => {
            for (let q = 0; q < 2; q++) {
              ctx.save(); ctx.translate(nx, ny); ctx.rotate(t * 0.0006 * (idx ? 1 : -1) + q * 1.2);
              ctx.strokeStyle = idx ? `rgba(0,200,255,${0.5 * fade})` : `rgba(60,150,255,${0.5 * fade})`;
              ctx.beginPath(); ctx.ellipse(0, 0, nodeR * 1.3, nodeR * 0.44, 0, 0, Math.PI * 2); ctx.stroke(); ctx.restore();
            }
          });
          ctx.globalCompositeOperation = 'source-over';
          const core = smooth((p - 0.4) / 0.15) * fade;
          if (core > 0.01) {
            const cr = unit * 0.72 * (0.7 + 0.3 * core);
            ctx.save();
            ctx.globalAlpha = core;
            if (!this.lite) { ctx.shadowColor = 'rgba(0,200,255,0.6)'; ctx.shadowBlur = 40; }
            const cg = ctx.createRadialGradient(cx - cr * 0.3, ny - cr * 0.35, cr * 0.1, cx, ny, cr);
            cg.addColorStop(0, '#13335F'); cg.addColorStop(1, '#06142B');
            ctx.fillStyle = cg; ctx.beginPath(); ctx.arc(cx, ny, cr, 0, Math.PI * 2); ctx.fill();
            ctx.shadowColor = 'transparent';
            ctx.strokeStyle = 'rgba(0,200,255,0.65)'; ctx.lineWidth = 1.2; ctx.stroke();
            ctx.restore();
            this.cfLogo(cx, ny, cr * 1.05, core);
          }
        }
        ctx.globalCompositeOperation = 'source-over';

        /* panneau « paiement confirmé » en 3D (0.62 → 1) : étape sécurisée, parcours validé, confirmation */
        const cp = smooth((p - 0.62) / 0.3);
        if (cp > 0.001) this.card(cx, cy - H * 0.17 + (1 - cp) * 40, W, H, cp, p, t, frozen);
      }

      /* panneau neutre « paiement confirmé » : une étape de paiement sécurisée et une transaction validée.
         Aucune carte, aucun numéro, aucune marque bancaire : Checkout Flow n'émet pas de carte et n'est pas un établissement de paiement. */
      card(cx, ccy, W, H, cp, p, t, frozen) {
        const ctx = this.ctx;
        const cw = Math.min(W * (W < 750 ? 0.62 : 0.3), 340, H * 0.44), ch = cw * 0.68;
        const ang = (1 - cp) * Math.PI * 0.5 + (frozen ? 0 : Math.sin(t * 0.0009) * 0.1 + this.px * 0.25);
        const sc = Math.cos(ang);
        const tilt = (frozen ? 0 : Math.sin(t * 0.0007) * 0.04) + this.py * 0.08;
        const ok = smooth((p - 0.86) / 0.1);
        const flow = clamp((p - 0.66) / 0.2, 0, 1); // panier → paiement → confirmation
        const r = ch * 0.1;
        ctx.save();
        ctx.globalAlpha = cp;
        ctx.translate(cx, ccy);
        ctx.transform(sc, tilt, Math.sin(ang) * 0.1, 1, 0, 0);
        if (!this.lite) { ctx.shadowColor = 'rgba(0,108,255,0.55)'; ctx.shadowBlur = 50; ctx.shadowOffsetY = 18; }
        const g = ctx.createLinearGradient(-cw / 2, -ch / 2, cw / 2, ch / 2);
        g.addColorStop(0, '#10284F'); g.addColorStop(1, '#071630');
        ctx.fillStyle = g; this.roundRect(-cw / 2, -ch / 2, cw, ch, r); ctx.fill();
        ctx.shadowColor = 'transparent'; ctx.shadowBlur = 0; ctx.shadowOffsetY = 0;
        ctx.strokeStyle = 'rgba(0,200,255,0.45)'; ctx.lineWidth = 1; ctx.stroke();
        ctx.save(); this.roundRect(-cw / 2, -ch / 2, cw, ch, r); ctx.clip();
        const sh = frozen ? 0.4 : ((t * 0.00025) % 1.6) - 0.3; // reflet discret
        const sg = ctx.createLinearGradient(-cw / 2 + sh * cw, -ch / 2, -cw / 2 + sh * cw + cw * 0.35, ch / 2);
        sg.addColorStop(0, 'rgba(255,255,255,0)'); sg.addColorStop(0.5, 'rgba(255,255,255,0.1)'); sg.addColorStop(1, 'rgba(255,255,255,0)');
        ctx.fillStyle = sg; ctx.fillRect(-cw / 2, -ch / 2, cw, ch);
        const x0 = -cw / 2 + cw * 0.08, iw = cw * 0.84, top = -ch / 2 + ch * 0.12;
        const fs = Math.max(10, Math.round(cw * 0.048));
        // en-tête : cadenas + « Paiement chiffré »
        this.lock(x0 + fs * 0.45, top + fs * 0.55, fs * 0.9);
        ctx.fillStyle = 'rgba(255,255,255,0.82)';
        ctx.font = `600 ${fs}px "CF Plex", "CF Plex Fallback", system-ui, sans-serif`;
        ctx.textBaseline = 'middle';
        if (this.lbl.secure) ctx.fillText(this.lbl.secure, x0 + fs * 1.4, top + fs * 0.6, iw - fs * 1.4);
        // récapitulatif abstrait (lignes sans montant réel)
        ctx.fillStyle = 'rgba(255,255,255,0.14)';
        this.roundRect(x0, top + ch * 0.2, iw * 0.56, ch * 0.05, 3); ctx.fill();
        this.roundRect(x0, top + ch * 0.3, iw * 0.36, ch * 0.05, 3); ctx.fill();
        ctx.fillStyle = 'rgba(255,255,255,0.32)';
        this.roundRect(x0 + iw * 0.72, top + ch * 0.19, iw * 0.28, ch * 0.07, 3); ctx.fill();
        // parcours : 3 étapes qui se valident
        const fy = top + ch * 0.5;
        const nodes = [0.06, 0.5, 0.94].map((k) => x0 + iw * k);
        ctx.strokeStyle = 'rgba(255,255,255,0.16)'; ctx.lineWidth = 2;
        ctx.beginPath(); ctx.moveTo(nodes[0], fy); ctx.lineTo(nodes[2], fy); ctx.stroke();
        ctx.strokeStyle = ok > 0.5 ? '#12B886' : '#00C8FF';
        ctx.beginPath(); ctx.moveTo(nodes[0], fy); ctx.lineTo(nodes[0] + (nodes[2] - nodes[0]) * flow, fy); ctx.stroke();
        nodes.forEach((nx, k) => {
          const reached = flow >= k / 2 - 0.001;
          ctx.beginPath(); ctx.arc(nx, fy, fs * 0.42, 0, Math.PI * 2);
          ctx.fillStyle = reached ? (ok > 0.5 ? '#12B886' : '#00C8FF') : '#0B1D38'; ctx.fill();
          ctx.strokeStyle = reached ? 'rgba(255,255,255,0.6)' : 'rgba(255,255,255,0.3)'; ctx.lineWidth = 1.2; ctx.stroke();
        });
        // bouton d'état : traitement → paiement confirmé
        const by = ch / 2 - ch * 0.29, bh = ch * 0.19;
        const bg = ctx.createLinearGradient(x0, 0, x0 + iw, 0);
        if (ok > 0.5) { bg.addColorStop(0, '#0FA877'); bg.addColorStop(1, '#12B886'); } else { bg.addColorStop(0, '#006CFF'); bg.addColorStop(1, '#00A6FF'); }
        ctx.fillStyle = bg; this.roundRect(x0, by, iw, bh, bh / 2); ctx.fill();
        ctx.fillStyle = '#fff';
        ctx.font = `700 ${fs}px "CF Plex", "CF Plex Fallback", system-ui, sans-serif`;
        ctx.textAlign = 'center';
        const lab = ok > 0.5 ? this.lbl.confirmed : this.lbl.processing;
        if (lab) ctx.fillText(lab, x0 + iw / 2 + (ok > 0.5 ? fs * 0.6 : 0), by + bh / 2 + 1, iw - fs * 2);
        if (ok > 0.5) {
          const tw = Math.min(ctx.measureText(lab).width, iw - fs * 2);
          const ix = x0 + iw / 2 - tw / 2 - fs * 0.35, iy = by + bh / 2;
          ctx.strokeStyle = '#fff'; ctx.lineWidth = 2; ctx.lineCap = 'round'; ctx.lineJoin = 'round';
          ctx.beginPath(); ctx.moveTo(ix - fs * 0.35, iy); ctx.lineTo(ix - fs * 0.1, iy + fs * 0.26); ctx.lineTo(ix + fs * 0.35, iy - fs * 0.26); ctx.stroke();
        }
        ctx.textAlign = 'start'; ctx.textBaseline = 'alphabetic';
        ctx.restore();
        ctx.restore();

        if (ok > 0.01) {
          ctx.save(); ctx.globalAlpha = ok; ctx.translate(cx + cw * 0.46, ccy - ch * 0.5); ctx.scale(0.6 + ok * 0.4, 0.6 + ok * 0.4);
          if (!this.lite) { ctx.shadowColor = 'rgba(18,184,134,0.7)'; ctx.shadowBlur = 30; }
          ctx.fillStyle = '#12B886'; ctx.beginPath(); ctx.arc(0, 0, 24, 0, Math.PI * 2); ctx.fill();
          ctx.shadowColor = 'transparent';
          ctx.strokeStyle = '#fff'; ctx.lineWidth = 3.5; ctx.lineCap = 'round'; ctx.lineJoin = 'round';
          ctx.beginPath(); ctx.moveTo(-10, 1); ctx.lineTo(-3, 8); ctx.lineTo(11, -7); ctx.stroke();
          ctx.restore();
        }
      }

      lock(x, y, s) {
        const ctx = this.ctx;
        ctx.save();
        ctx.strokeStyle = '#00C8FF'; ctx.lineWidth = Math.max(1.4, s * 0.14);
        ctx.beginPath(); ctx.arc(x, y - s * 0.12, s * 0.28, Math.PI, 0); ctx.stroke();
        ctx.fillStyle = '#00C8FF'; this.roundRect(x - s * 0.42, y - s * 0.12, s * 0.84, s * 0.62, s * 0.12); ctx.fill();
        ctx.restore();
      }

      drawFrame(now, still) {
        const ctx = this.ctx;
        if (!ctx || !this.W) return;
        const W = this.W, H = this.H;
        const m = motion();
        const soft = m === 'soft';
        const frozen = still || m === 'off';
        this.px += (this.mx - this.px) * 0.05;
        this.py += (this.my - this.py) * 0.05;
        const cx = W / 2 + (frozen ? 0 : this.px * 24);
        const cy = this.cy + (frozen ? 0 : this.py * 12);
        const intro = frozen ? 1 : clamp((now - this.t0) / 2600, 0, 1);
        const join = smooth(intro);
        const t = frozen ? 0 : now;
        const f = Math.min(W, H) * 0.9;
        ctx.clearRect(0, 0, W, H);

        /* fond */
        const bg = ctx.createRadialGradient(cx, cy, 0, cx, cy, Math.max(W, H) * 0.8);
        bg.addColorStop(0, '#07183A'); bg.addColorStop(0.55, '#040E20'); bg.addColorStop(1, '#020A14');
        ctx.fillStyle = bg; ctx.fillRect(0, 0, W, H);

        /* tunnel : la caméra avance doucement, le défilement l'accélère */
        const cam = this.sp * 5 + (frozen ? 0.8 : t * (soft ? 0.00005 : 0.0001)) + (1 - join) * 1.2;
        ctx.globalCompositeOperation = 'lighter';
        const D = this.depth;
        const tcy = H * 0.42;
        for (let i = 0; i < this.stars.length; i++) {
          const s = this.stars[i];
          const z = ((s.z - cam) % D + D) % D + 0.05;
          const sx = cx + (s.x / z) * f * 0.5;
          const sy = tcy + (s.y / z) * f * 0.5;
          if (sx < -40 || sx > W + 40 || sy < -40 || sy > H + 40) continue;
          const near = 1 - z / D;
          const r = Math.max(0.4, s.s * near * 2.4);
          const al = near * near * 0.85;
          ctx.fillStyle = s.c ? `rgba(0,200,255,${al})` : `rgba(40,130,255,${al})`;
          if (!frozen && !soft && near > 0.45 && intro < 1) {
            const z2 = z + 0.35 * (1 - join);
            const tx = cx + (s.x / z2) * f * 0.5, ty = tcy + (s.y / z2) * f * 0.5;
            ctx.strokeStyle = ctx.fillStyle; ctx.lineWidth = r; ctx.lineCap = 'round';
            ctx.beginPath(); ctx.moveTo(tx, ty); ctx.lineTo(sx, sy); ctx.stroke();
          } else {
            ctx.beginPath(); ctx.arc(sx, sy, r, 0, Math.PI * 2); ctx.fill();
          }
        }

        if (!this.withLogos) { ctx.globalCompositeOperation = 'source-over'; return; }

        /* deux nœuds (Shopify / SumUp) qui convergent vers le logo Checkout Flow */
        const unit = Math.min(W * 0.12, this.zoneH * 0.32, 64);
        const finalGap = unit * 2.35;
        const spread = finalGap + (Math.min(W * 0.4, 560) - finalGap) * (1 - join);
        const bob = frozen ? 0 : Math.sin(t * 0.0012) * 4 * (1 - join);
        const lx = cx - spread, rx = cx + spread, ny = cy + bob;
        const nodeR = unit * 0.62;

        ctx.lineCap = 'round';
        for (let k = 0; k < 5; k++) {
          const off = (k - 2) * (unit * 0.2) * (1 - join * 0.7);
          const amp = (k % 2 ? -1 : 1) * unit * 0.9 * (1 - join * 0.85);
          const c1x = cx - spread * 0.35, c2x = cx + spread * 0.35;
          ctx.strokeStyle = `rgba(0,200,255,${0.16 + 0.14 * join})`;
          ctx.lineWidth = 1.2;
          ctx.beginPath();
          ctx.moveTo(lx, ny + off);
          ctx.bezierCurveTo(c1x, ny + off + amp, c2x, ny + off - amp, rx, ny + off);
          ctx.stroke();
          if (!frozen) {
            const u = ((t * (soft ? 0.00018 : 0.00032) + k * 0.21) % 1);
            const iu = 1 - u;
            const bx = iu * iu * iu * lx + 3 * iu * iu * u * c1x + 3 * iu * u * u * c2x + u * u * u * rx;
            const by = iu * iu * iu * (ny + off) + 3 * iu * iu * u * (ny + off + amp) + 3 * iu * u * u * (ny + off - amp) + u * u * u * (ny + off);
            this.glow(bx, by, 8 + 6 * join, '160,235,255', 0.85);
          }
        }
        this.glow(lx, ny, nodeR * 3, '0,108,255', 0.5);
        this.glow(rx, ny, nodeR * 3, '0,200,255', 0.5);

        /* onde de connexion à l'arrivée, puis pulsation lente */
        const since = (now - this.t0) / 1000 - 2.4;
        if (!frozen && since > 0) {
          const period = soft ? 6 : 4.2;
          const w = (since % period) / (period * 0.55);
          if (w < 1) {
            const wr = smooth(w) * Math.max(W, H) * 0.55;
            ctx.strokeStyle = `rgba(0,200,255,${(1 - w) * (since < period ? 0.8 : 0.35)})`;
            ctx.lineWidth = 2.5 * (1 - w) + 0.5;
            ctx.beginPath(); ctx.ellipse(cx, cy, wr, wr * 0.5, 0, 0, Math.PI * 2); ctx.stroke();
          }
        }
        this.glow(cx, cy, unit * 2.6, '120,220,255', 0.18 + 0.4 * join);

        ctx.globalCompositeOperation = 'source-over';
        this.badge(lx, ny, nodeR, this.imgs.shop, '#FFFFFF', 1, false);
        this.badge(rx, ny, nodeR, this.imgs.sum, '#000000', 1, true);

        /* anneaux en orbite */
        ctx.globalCompositeOperation = 'lighter';
        ctx.lineWidth = 1.3;
        [lx, rx].forEach((nx, idx) => {
          for (let q = 0; q < 2; q++) {
            ctx.save(); ctx.translate(nx, ny); ctx.rotate(t * 0.0006 * (idx ? 1 : -1) + q * 1.2);
            ctx.strokeStyle = idx ? 'rgba(0,200,255,0.45)' : 'rgba(60,150,255,0.45)';
            ctx.beginPath(); ctx.ellipse(0, 0, nodeR * 1.3, nodeR * 0.44, 0, 0, Math.PI * 2); ctx.stroke(); ctx.restore();
          }
        });
        ctx.globalCompositeOperation = 'source-over';

        /* cœur Checkout Flow */
        const core = smooth((intro - 0.55) / 0.45);
        if (core > 0.01) {
          const cr = unit * 0.72 * (0.7 + 0.3 * core) * (frozen ? 1 : 1 + Math.sin(t * 0.002) * 0.02);
          ctx.save();
          ctx.globalAlpha = core;
          if (!this.lite) { ctx.shadowColor = 'rgba(0,200,255,0.6)'; ctx.shadowBlur = 40; }
          const cg = ctx.createRadialGradient(cx - cr * 0.3, cy - cr * 0.35, cr * 0.1, cx, cy, cr);
          cg.addColorStop(0, '#13335F'); cg.addColorStop(1, '#06142B');
          ctx.fillStyle = cg; ctx.beginPath(); ctx.arc(cx, cy, cr, 0, Math.PI * 2); ctx.fill();
          ctx.shadowColor = 'transparent';
          ctx.strokeStyle = 'rgba(0,200,255,0.65)'; ctx.lineWidth = 1.2; ctx.stroke();
          ctx.restore();
          this.cfLogo(cx, cy, cr * 1.05, core);
        }
      }
    }
    customElements.define('cf-hero-scene', CFHeroScene);
  };
  if (window.CF) start(); else document.addEventListener('cf:ready', start, { once: true });
})();
